/**
 * Settings > Tags Page (Story 3.6)
 *
 * Tenant-level tag management:
 * - List all tags with color swatch, name, client count
 * - Inline edit tag name/color
 * - Delete tag with confirmation
 * - Create new tag
 *
 * RBAC: Owner/Admin only (MANAGE_CLIENTS permission).
 */

import { useState, useRef, useEffect, useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { ReportBreadcrumb } from "../../../components/reports/ReportBreadcrumb";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@provenleap/ui";
import { apiGet, apiPost, apiPatch, apiDelete } from "../../../lib/apiClient";
import { usePermission, Actions } from "../../../hooks/usePermission";
import { TAG_COLORS } from "../../../components/clients/types";

export const Route = createFileRoute("/_authenticated/settings/tags")({
  component: TagsSettingsPage,
});

interface Tag {
  id: string;
  name: string;
  color: string;
  clientCount: number;
}

function TagsSettingsPage() {
  const { hasPermission } = usePermission();
  const queryClient = useQueryClient();
  const canManage = hasPermission(Actions.MANAGE_CLIENTS);

  // Toast state
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  function showToast(type: "success" | "error", message: string) {
    setToast({ type, message });
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    if (type !== "error") {
      toastTimeoutRef.current = setTimeout(() => setToast(null), 3000);
    }
  }

  // Create form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState<string>(TAG_COLORS[0]!.value);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");

  // Delete confirmation state
  const [deleteTag, setDeleteTag] = useState<Tag | null>(null);

  // Fetch tags
  const { data: rawTags = [], isLoading } = useQuery<Tag[]>({
    queryKey: ["tags"],
    queryFn: async () => {
      const res = await apiGet("/api/tags");
      if (!res.ok) throw new Error("Failed to load tags");
      const json = await res.json();
      return json.data;
    },
  });

  const tags = useMemo(
    () => [...rawTags].sort((a, b) => a.name.localeCompare(b.name)),
    [rawTags]
  );

  // Create tag mutation
  const createMutation = useMutation({
    mutationFn: async ({ name, color }: { name: string; color: string }) => {
      const res = await apiPost("/api/tags", { name, color });
      if (!res.ok) {
        const err = await res.json();
        throw { status: res.status, ...err };
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] });
      setNewName("");
      setNewColor(TAG_COLORS[0]!.value);
      setShowCreateForm(false);
      showToast("success", "Tag created");
    },
    onError: (err: any) => {
      if (err?.type?.includes?.("duplicate-tag")) {
        showToast("error", "A tag with this name already exists");
      } else {
        showToast("error", err?.title ?? "Failed to create tag");
      }
    },
  });

  // Update tag mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, name, color }: { id: string; name: string; color: string }) => {
      const res = await apiPatch(`/api/tags/${id}`, { name, color });
      if (!res.ok) {
        const err = await res.json();
        throw { status: res.status, ...err };
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      setEditingId(null);
      showToast("success", "Tag updated");
    },
    onError: (err: any) => {
      if (err?.type?.includes?.("duplicate-tag")) {
        showToast("error", "A tag with this name already exists");
      } else {
        showToast("error", err?.title ?? "Failed to update tag");
      }
    },
  });

  // Delete tag mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiDelete(`/api/tags/${id}`);
      if (!res.ok) {
        const err = await res.json();
        throw { status: res.status, ...err };
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      setDeleteTag(null);
      showToast("success", "Tag deleted");
    },
    onError: (err: any) => {
      showToast("error", err?.title ?? "Failed to delete tag");
    },
  });

  function handleStartEdit(tag: Tag) {
    setEditingId(tag.id);
    setEditName(tag.name);
    setEditColor(tag.color);
  }

  function handleSaveEdit() {
    if (!editingId || !editName.trim()) return;
    updateMutation.mutate({ id: editingId, name: editName.trim(), color: editColor });
  }

  function handleCancelEdit() {
    setEditingId(null);
    setEditName("");
    setEditColor("");
  }

  function handleCreate() {
    const trimmed = newName.trim();
    if (!trimmed) return;
    createMutation.mutate({ name: trimmed, color: newColor });
  }

  // RBAC gate
  if (!canManage) {
    return (
      <div className="p-6 max-w-[1200px] mx-auto" data-testid="tags-forbidden">
        <ReportBreadcrumb items={[{ label: "Settings", to: "/settings" }, { label: "Tags" }]} />
        <p className="text-muted-foreground">You do not have permission to manage tags.</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1200px] mx-auto">
      <ReportBreadcrumb items={[{ label: "Settings", to: "/settings" }, { label: "Tags" }]} />
      <div className="flex items-center justify-end mb-6">
        {!showCreateForm && (
          <Button
            onClick={() => setShowCreateForm(true)}
            data-testid="create-tag-button"
          >
            Create Tag
          </Button>
        )}
      </div>

      {/* Create tag form */}
      {showCreateForm && (
        <Card className="mb-6" data-testid="create-tag-form">
          <CardHeader>
            <CardTitle>Create New Tag</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <Input
                placeholder="Tag name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                maxLength={50}
                data-testid="new-tag-name"
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreate();
                  if (e.key === "Escape") setShowCreateForm(false);
                }}
              />
              <div>
                <label className="text-sm font-medium mb-1 block">Color</label>
                <div className="grid grid-cols-6 gap-2" data-testid="create-color-palette">
                  {TAG_COLORS.map((c) => (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => setNewColor(c.value)}
                      className={`h-8 w-8 rounded-full border-2 ${
                        newColor === c.value ? "border-foreground" : "border-transparent"
                      }`}
                      style={{ backgroundColor: c.value }}
                      title={c.label}
                      aria-label={`Select ${c.label} color`}
                    />
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleCreate}
                  disabled={!newName.trim() || createMutation.isPending}
                  data-testid="confirm-create-tag"
                >
                  {createMutation.isPending ? "Creating..." : "Create"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewName("");
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tag list */}
      <Card>
        <CardHeader>
          <CardTitle>All Tags</CardTitle>
          <CardDescription>
            Manage tags for organizing your clients
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading && (
            <div className="flex items-center justify-center py-4" data-testid="tags-loading">
              <svg
                className="animate-spin h-6 w-6 text-primary"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                role="img"
                aria-label="Loading"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                <path className="opacity-90" d="M22 12a10 10 0 00-10-10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </div>
          )}
          {!isLoading && tags.length === 0 && (
            <p className="text-sm text-muted-foreground" data-testid="tags-empty">
              No tags yet. Create your first tag to start organizing clients.
            </p>
          )}
          {!isLoading && tags.length > 0 && (
            <div className="space-y-2" data-testid="tags-list">
              {tags.map((tag) => (
                <div
                  key={tag.id}
                  className="flex items-center gap-3 py-2 px-3 rounded-md border"
                  data-testid="tag-row"
                >
                  {editingId === tag.id ? (
                    /* Inline edit mode */
                    <div className="flex-1 space-y-2" data-testid="tag-edit-form">
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        maxLength={50}
                        data-testid="edit-tag-name"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSaveEdit();
                          if (e.key === "Escape") handleCancelEdit();
                        }}
                      />
                      <div className="grid grid-cols-6 gap-1" data-testid="edit-color-palette">
                        {TAG_COLORS.map((c) => (
                          <button
                            key={c.value}
                            type="button"
                            onClick={() => setEditColor(c.value)}
                            className={`h-6 w-6 rounded-full border-2 ${
                              editColor === c.value ? "border-foreground" : "border-transparent"
                            }`}
                            style={{ backgroundColor: c.value }}
                            title={c.label}
                            aria-label={`Select ${c.label} color`}
                          />
                        ))}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          onClick={handleSaveEdit}
                          disabled={!editName.trim() || updateMutation.isPending}
                          data-testid="save-edit-tag"
                        >
                          {updateMutation.isPending ? "Saving..." : "Save"}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleCancelEdit}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* Display mode */
                    <>
                      <span
                        className="h-4 w-4 rounded-full shrink-0"
                        style={{ backgroundColor: tag.color }}
                        data-testid="tag-color-swatch"
                      />
                      <span className="flex-1 font-medium" data-testid="tag-name">
                        {tag.name}
                      </span>
                      <span className="text-sm text-muted-foreground" data-testid="tag-client-count">
                        {tag.clientCount} {tag.clientCount === 1 ? "client" : "clients"}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleStartEdit(tag)}
                        data-testid="edit-tag-button"
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive"
                        onClick={() => setDeleteTag(tag)}
                        data-testid="delete-tag-button"
                      >
                        Delete
                      </Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTag} onOpenChange={(open) => !open && setDeleteTag(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {deleteTag?.name}?</DialogTitle>
            <DialogDescription>
              This will remove it from all {deleteTag?.clientCount ?? 0} clients. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTag(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteTag && deleteMutation.mutate(deleteTag.id)}
              disabled={deleteMutation.isPending}
              data-testid="confirm-delete-tag"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Toast notification */}
      {toast && (
        <div
          className={`fixed bottom-4 right-4 z-50 rounded-md px-4 py-3 text-sm shadow-lg flex items-center gap-2 ${
            toast.type === "success"
              ? "bg-green-50 border border-green-200 text-green-800"
              : "bg-destructive/10 border border-destructive/20 text-destructive"
          }`}
          role={toast.type === "error" ? "alert" : "status"}
          data-testid="toast"
        >
          {toast.message}
          {toast.type === "error" && (
            <button
              type="button"
              onClick={() => setToast(null)}
              className="ml-2 font-medium underline text-xs"
              aria-label="Dismiss error"
            >
              Dismiss
            </button>
          )}
        </div>
      )}
    </div>
  );
}
